import { useState, useEffect } from "react";
import { HiXMark } from "react-icons/hi2";
import { FiEdit } from "react-icons/fi";
import { RiDeleteBin6Line } from "react-icons/ri";
import axios from "axios";
import { toast } from "react-toastify";
import { MdKeyboardArrowLeft, MdKeyboardArrowRight } from "react-icons/md";
import ConfirmActionModal from "../ConfirmDeleteModal";
import EditMedicineModal from "../EditMedicineModal";

const API_URL = "https://api.muktihospital.com/api/medicine";
const PURCHASE_API = "https://api.muktihospital.com/api/medicine-purchase";
const BAD_STOCK_API = "https://api.muktihospital.com/api/bad-stock";
const PURCHASE_DELETE_API = "https://api.muktihospital.com/api/medicine-purchase";
const BAD_STOCK_DELETE_API = "https://api.muktihospital.com/api/bad-stock";
const API_KEY =
  "caf56e69405fe970f918e99ce86a80fbf0a7d728cca687e8a433b817411a6079";

const EditBadStockModal = ({ isOpen, onClose, onSave, badStock }) => {
  const [formData, setFormData] = useState({
    medicineName: badStock?.medicineName || "",
    batchNo: badStock?.batchNo?.toString() || "", // Support batchNo: "0"
    expiryDate: badStock?.expiryDate
      ? new Date(badStock.expiryDate).toISOString().split("T")[0]
      : "",
    outwardDate: badStock?.outwardDate
      ? new Date(badStock.outwardDate).toISOString().split("T")[0]
      : "",
    qty: badStock?.qty || "",
    note: badStock?.note || "",
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.qty || parseInt(formData.qty, 10) <= 0) {
      toast.error("Valid quantity is required.");
      return;
    }
    try {
      const payload = {
        ...formData,
        qty: parseInt(formData.qty, 10),
        itemIndex: badStock?.itemIndex || 0, // Include itemIndex
      };
      // console.log('Updating bad stock:', payload);
      const response = await axios.put(
        `${BAD_STOCK_API}/${badStock.id}`,
        payload,
        {
          headers: { "x-api-key": API_KEY },
        }
      );
      toast.success("Bad stock updated successfully!");
      onSave(response.data);
      onClose();
    } catch (err) {
      console.error("Error updating bad stock:", err);
      toast.error(err.response?.data?.error || "Failed to update bad stock.");
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md">
        <div className="flex justify-between items-center mb-4 border-b border-dashed border-M-text-color/50 pt-6 pb-4 px-6">
          <h2 className="text-xl font-bold text-gray-700">Edit Bad Stock</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
          >
            <HiXMark className="w-6 h-6" />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="pb-6 pt-2 px-6">
          <div className="mb-4">
            <label className="label">Medicine Name</label>
            <input
              type="text"
              name="medicineName"
              value={formData.medicineName}
              className="input-field bg-gray-100 cursor-not-allowed"
              disabled
            />
          </div>
          <div className="mb-4">
            <label className="label">Batch No</label>
            <input
              type="text"
              name="batchNo"
              value={formData.batchNo}
              className="input-field bg-gray-100 cursor-not-allowed"
              disabled
            />
          </div>
          <div className="mb-4">
            <label className="label">Expiry Date</label>
            <input
              type="date"
              name="expiryDate"
              value={formData.expiryDate}
              className="input-field bg-gray-100 cursor-not-allowed"
              disabled
            />
          </div>
          <div className="mb-4">
            <label className="label">Outward Date</label>
            <input
              type="date"
              name="outwardDate"
              value={formData.outwardDate}
              className="input-field bg-gray-100 cursor-not-allowed"
              disabled
            />
          </div>
          <div className="mb-4">
            <label className="label">Quantity</label>
            <input
              type="number"
              name="qty"
              value={formData.qty}
              onChange={handleChange}
              className="input-field"
              required
              min="1"
            />
          </div>
          <div className="mb-4">
            <label className="label">Note</label>
            <textarea
              name="note"
              value={formData.note}
              onChange={handleChange}
              className="input-field"
            />
          </div>
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={onClose}
              className="bg-[#E7633D]/90 text-xs md:text-sm text-white py-2 px-4 sm:px-6 rounded-md hover:bg-[#E7633D] transition-all duration-300"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-M-primary-color/90 text-xs md:text-sm text-white py-2 px-4 sm:px-6 rounded-md hover:bg-M-primary-color transition-all duration-200"
            >
              Save
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const MedicineDetailsModal = ({
  isOpen,
  onClose,
  medicineId,
  medicine,
  onDelete,
  onSave,
  categories,
  groups,
  companies,
  units,
  onRefresh,
}) => {
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isEditBadStockModalOpen, setIsEditBadStockModalOpen] = useState(false);
  const [selectedBadStock, setSelectedBadStock] = useState(null);
  const [isLoadingStock, setIsLoadingStock] = useState(false);
  const [isLoadingBadStock, setIsLoadingBadStock] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [activeTab, setActiveTab] = useState("stock");
  const [stockData, setStockData] = useState([]);
  const [badStockData, setBadStockData] = useState([]);
  const [stockPage, setStockPage] = useState(1);
  const [badStockPage, setBadStockPage] = useState(1);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [stockSearch, setStockSearch] = useState("");
  const [badStockSearch, setBadStockSearch] = useState("");
  const itemsPerPage = 10;

 useEffect(() => {
    // 1. Fetch Stock Data
    const fetchStockData = async () => {
      setIsLoadingStock(true);
      try {
        const response = await axios.get(`${PURCHASE_API}?medicineId=${medicine.id}&limit=100`, {
          headers: { "x-api-key": API_KEY },
        });

        // Flatten the nested items array
        const filteredStock = response.data.purchases
          .flatMap((purchase) => {
            // Ensure items is an array (Handle both legacy string data and new relation data)
            let items = [];
            if (Array.isArray(purchase.items)) {
              items = purchase.items;
            } else if (typeof purchase.items === "string") {
              try { items = JSON.parse(purchase.items); } catch (e) { items = []; }
            }

            return items.map((item, index) => ({
              ...item,
              purchaseId: purchase.id,
              billNo: purchase.billNo,
              itemIndex: index,
            }));
          })
          // Filter to show only specific medicine items
          .filter((item) => parseInt(item.medicineId) === parseInt(medicine.id));

        setStockData(filteredStock);
      } catch (err) {
        console.error("Error fetching stock data:", err);
        // Don't show toast on 404 (just empty list)
        if (err.response?.status !== 404) toast.error("Failed to fetch stock data.");
      } finally {
        setIsLoadingStock(false);
      }
    };

    // 2. Fetch Bad Stock Data
    const fetchBadStockData = async () => {
      setIsLoadingBadStock(true);
      try {
        const response = await axios.get(BAD_STOCK_API, {
          headers: { "x-api-key": API_KEY },
        });
        
        // Filter Bad Stock by Medicine ID (Safer) or Name (Fallback)
        const filteredBadStock = response.data.filter((badStock) => {
            // Try matching by ID first
            if (badStock.medicineId && medicine.id) {
                return parseInt(badStock.medicineId) === parseInt(medicine.id);
            }
            // Fallback to Name match
            const badStockName = badStock.medicineName?.toLowerCase().trim();
            const currentName = medicine.medicineName?.toLowerCase().trim();
            return badStockName === currentName;
        });

        setBadStockData(filteredBadStock);
      } catch (err) {
        console.error("Error fetching bad stock data:", err);
      } finally {
        setIsLoadingBadStock(false);
      }
    };

    if (isOpen && medicine?.id) {
      fetchStockData();
      fetchBadStockData();
    }
  }, [isOpen, medicine]);

  useEffect(() => {
    setStockSearch("");
    setBadStockSearch("");
    setStockPage(1);
    setBadStockPage(1);
  }, [activeTab]);

  const filteredStockData = stockData.filter((item) =>
    item.billNo?.toLowerCase().includes(stockSearch.toLowerCase())
  );

  const filteredBadStockData = badStockData.filter((item) =>
    item.batchNo?.toString().toLowerCase().includes(badStockSearch.toLowerCase())
  );

  const medicineDetails = medicine;

  if (!isOpen || !medicineDetails) return null;

  const {
    id,
    medicineName,
    medicineCompany,
    medicineCategory,
    medicineGroup,
    unit,
    vat,
    rackNumber,
    medicinePhoto,
  } = medicineDetails;

  const handleEdit = () => {
    setIsEditModalOpen(true);
  };

  const handleDelete = () => {
    setDeleteModalOpen(true);
    setItemToDelete(null);
  };

  const confirmDelete = async (ids) => {
    setIsDeleting(true);
    try {
      await axios.delete(`${API_URL}/delete/${ids}`, {
        headers: { "x-api-key": API_KEY },
      });
      onDelete(ids);
      onRefresh();
      onClose();
    } catch (err) {
      console.error("Error deleting medicine:", err);
      toast.error(err.response?.data?.error || "Failed to delete medicine.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRowDelete = async (ids) => {
    const parsedId = parseInt(ids);
    if (isNaN(parsedId)) {
      console.error("Invalid item ID:", ids);
      toast.error("Invalid item ID.");
      return;
    }

    setIsDeleting(true);
    try {
      if (activeTab === "stock") {
        const stockItem = stockData.find(item => item.purchaseId === parsedId);
        if (!stockItem) throw new Error("Stock item not found.");
        // console.log(`Deleting stock item with purchaseId: ${parsedId}, itemIndex: ${stockItem.itemIndex}`);
        await axios.delete(`${PURCHASE_DELETE_API}/${parsedId}`, {
          headers: { "x-api-key": API_KEY },
          data: { itemIndex: stockItem.itemIndex },
        });
        setStockData((prev) => prev.filter((item) => item.purchaseId !== parsedId));
        toast.success("Stock item deleted successfully.");
      } else {
        // console.log(`Deleting bad stock item with ID: ${parsedId}`);
        await axios.delete(`${BAD_STOCK_DELETE_API}/${parsedId}`, {
          headers: { "x-api-key": API_KEY },
        });
        setBadStockData((prev) => prev.filter((item) => item.id !== parsedId));
        toast.success("Bad stock item deleted successfully.");
      }
    } catch (err) {
      console.error(`Error deleting item ID ${parsedId}:`, err);
      toast.error(err.response?.data?.error || "Failed to delete item.");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleEditBadStock = (badStock) => {
    setSelectedBadStock(badStock);
    setIsEditBadStockModalOpen(true);
  };

  const handleSaveBadStock = (updatedBadStock) => {
    setBadStockData((prev) =>
      prev.map((item) =>
        item.id === updatedBadStock.id ? updatedBadStock : item
      )
    );
  };

  const paginateData = (data, page) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  const stockTableContent = (currentStockData) => (
    <div className="overflow-x-auto print-wrapper">
      <table className="min-w-[1000px] w-full divide-y divide-gray-200 print-table">
        <thead>
          <tr className="bg-gray-100">
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
              No.
            </th>
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
              Bill No
            </th>
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
              Batch No
            </th>
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
              Expiry Date
            </th>
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
              Quantity
            </th>
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
              Available Qty
            </th>
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
              Sale Price
            </th>
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
        {currentStockData.length === 0 ? (
            <tr className="print-hidden">
              <td
                colSpan="8"
                className="text-center py-4 capitalize font-inter font-normal text-sm text-M-heading-color"
              >
                No stock data available
              </td>
            </tr>
          ) : (
            currentStockData.map((item, i) => (
              <tr key={`${item.purchaseId}-${item.itemIndex}`} className="border-b hover:bg-gray-50">
                <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                  {i + 1}
                </td>
                <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                  {item.billNo || "N/A"}
                </td>
                <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                  {item.batchNo?.toString() || "N/A"}
                </td>
                <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                  {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString("en-US") : "N/A"}
                </td>
                <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                  {item.quantity || 0}
                </td>
                <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                  {item.availableQuantity || 0}
                </td>
                <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                  {item.salePrice || item.boxPrice || 0}
                </td>
                <td className="p-3 text-left space-x-2 print-hidden-in-print">
                  <button
                    onClick={() => {
                      setItemToDelete(item.purchaseId);
                      setDeleteModalOpen(true);
                    }}
                    className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                    disabled={isDeleting}
                  >
                    <RiDeleteBin6Line />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const badStockTableContent = (currentBadStockData) => (
    <div className="overflow-x-auto print-wrapper">
      <table className="min-w-[1000px] w-full divide-y divide-gray-200 print-table">
        <thead>
          <tr className="bg-gray-100">
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
              No.
            </th>
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
              Batch No
            </th>
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
              Expiry Date
            </th>
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
              Outward Date
            </th>
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
              Quantity
            </th>
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3">
              Note
            </th>
            <th className="text-left uppercase font-inter font-medium text-sm text-M-heading-color p-3 print-hidden-in-print">
              Actions
            </th>
          </tr>
        </thead>
        <tbody>
          {currentBadStockData.length === 0 ? (
            <tr className="print-hidden">
              <td
                colSpan="7"
                className="text-center py-4 capitalize font-inter font-normal text-sm text-M-heading-color"
              >
                No bad stock data available
              </td>
            </tr>
          ) : (
            currentBadStockData.map((item, i) => (
              <tr key={item.id} className="border-b hover:bg-gray-50">
                <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                  {i + 1}
                </td>
                <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                  {item.batchNo?.toString() || "N/A"}
                </td>
                <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                  {item.expiryDate ? new Date(item.expiryDate).toLocaleDateString("en-US") : "N/A"}
                </td>
                <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                  {item.outwardDate ? new Date(item.outwardDate).toLocaleDateString("en-US") : "N/A"}
                </td>
                <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                  {item.qty || 0}
                </td>
                <td className="text-left capitalize font-inter font-normal text-sm text-M-heading-color py-5 px-3">
                  {item.note || "N/A"}
                </td>
                <td className="p-3 text-left space-x-2 print-hidden-in-print">
                  {/* <button
                    onClick={() => handleEditBadStock(item)}
                    className="size-7 rounded-full bg-blue-100 text-blue-600 text-sm inline-flex items-center justify-center hover:bg-blue-600 hover:text-white transition-all duration-300"
                    disabled={isDeleting}
                  >
                    <FiEdit />
                  </button> */}
                  <button
                    onClick={() => {
                      setItemToDelete(item.id);
                      setDeleteModalOpen(true);
                    }}
                    className="size-7 rounded-full bg-M-secondary-color/10 text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                    disabled={isDeleting}
                  >
                    <RiDeleteBin6Line />
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const renderPagination = (data, page, setPage) => {
    const totalPages = Math.ceil(data.length / itemsPerPage);
    const maxVisiblePages = 5;
    let startPage = Math.max(1, page - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }
    const pageNumbers = [];
    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(i);
    }

    return (
      totalPages > 1 && (
        <div className="p-5 text-end print-hidden">
          <div className="inline-flex items-center justify-between w-full">
            <p className="text-sm text-gray-600">
              Records: {page * itemsPerPage - itemsPerPage + 1} to{" "}
              {Math.min(page * itemsPerPage, data.length)} of {data.length}
            </p>
            <ul className="inline-flex items-center justify-end border-collapse border border-M-text-color/20 rounded-md overflow-hidden">
              <li>
                <button
                  className={`table-pagination-item ${
                    page === 1 ? "disabled" : ""
                  }`}
                  onClick={() => setPage((prev) => prev - 1)}
                  disabled={page === 1}
                >
                  <MdKeyboardArrowLeft />
                </button>
              </li>
              {startPage > 1 && (
                <>
                  <li>
                    <button
                      className="table-pagination-item"
                      onClick={() => setPage(1)}
                    >
                      1
                    </button>
                  </li>
                  {startPage > 2 && (
                    <li>
                      <span className="table-pagination-item disabled">
                        ...
                      </span>
                    </li>
                  )}
                </>
              )}
              {pageNumbers.map((pageNum) => (
                <li key={pageNum}>
                  <button
                    className={`table-pagination-item ${
                      page === pageNum ? "active" : ""
                    }`}
                    onClick={() => setPage(pageNum)}
                  >
                    {pageNum}
                  </button>
                </li>
              ))}
              {endPage < totalPages && (
                <>
                  {endPage < totalPages - 1 && (
                    <li>
                      <span className="table-pagination-item disabled">
                        ...
                      </span>
                    </li>
                  )}
                  <li>
                    <button
                      className="table-pagination-item"
                      onClick={() => setPage(totalPages)}
                    >
                      {totalPages}
                    </button>
                  </li>
                </>
              )}
              <li>
                <button
                  onClick={() => setPage((prev) => prev + 1)}
                  disabled={page === totalPages}
                  className={`table-pagination-item ${
                    page === totalPages ? "disabled" : ""
                  }`}
                >
                  <MdKeyboardArrowRight />
                </button>
              </li>
            </ul>
          </div>
        </div>
      )
    );
  };

  return (
    <>
      <div className="fixed inset-0 z-40 flex items-start sm:items-center justify-center bg-black bg-opacity-40 px-4 sm:px-0 overflow-y-auto">
        <div className="bg-white shadow-lg w-full my-4 sm:my-0 max-h-[100vh] h-full overflow-y-auto rounded-lg">
          <div>
            <div className="flex items-center justify-between gap-3 flex-wrap bg-gray-200 border-b border-dashed border-gray-300 p-4 sm:p-5 print-hidden">
              <div className="flex items-center gap-3">
                <h2 className="text-base font-medium text-gray-700 flex items-center gap-2">
                  Medicine Details
                </h2>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={handleEdit}
                  className="size-7 rounded-full bg-white text-M-Green-color text-sm inline-flex items-center justify-center hover:bg-M-Green-color hover:text-white transition-all duration-300"
                  title="Edit"
                  disabled={isDeleting}
                >
                  <FiEdit size={16} />
                </button>
                <button
                  onClick={handleDelete}
                  className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                  title="Delete"
                  disabled={isDeleting}
                >
                  <RiDeleteBin6Line size={16} />
                </button>
                <button
                  onClick={() => {
                    toast.dismiss();
                    onClose();
                  }}
                  className="size-7 rounded-full bg-white text-M-secondary-color text-sm inline-flex items-center justify-center hover:bg-M-secondary-color hover:text-white transition-all duration-300"
                  title="Close"
                >
                  <HiXMark size={16} />
                </button>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm text-M-text-color py-1">
                    <strong className="w-32 sm:w-48 inline-block">
                      Medicine Name
                    </strong>
                    :{" "}
                    <span className="text-black font-normal">
                      {medicineName || "N/A"}
                    </span>
                  </p>
                  <p className="text-sm text-M-text-color py-1">
                    <strong className="w-32 sm:w-48 inline-block">
                      Medicine Company
                    </strong>
                    :{" "}
                    <span className="text-black font-normal">
                      {medicineCompany || "N/A"}
                    </span>
                  </p>
                  <p className="text-sm text-M-text-color py-1">
                    <strong className="w-32 sm:w-48 inline-block">
                      Medicine Category
                    </strong>
                    :{" "}
                    <span className="text-black font-normal">
                      {medicineCategory || "N/A"}
                    </span>
                  </p>
                  <p className="text-sm text-M-text-color py-1">
                    <strong className="w-32 sm:w-48 inline-block">
                      Medicine Group
                    </strong>
                    :{" "}
                    <span className="text-black font-normal">
                      {medicineGroup || "N/A"}
                    </span>
                  </p>
                  <p className="text-sm text-M-text-color py-1">
                    <strong className="w-32 sm:w-48 inline-block">Unit</strong>:{" "}
                    <span className="text-black font-normal">
                      {unit || "N/A"}
                    </span>
                  </p>
                  <p className="text-sm text-M-text-color py-1">
                    <strong className="w-32 sm:w-48 inline-block">Rack Number</strong>
                    :{" "}
                    <span className="text-black font-normal">
                      {rackNumber || "N/A"}
                    </span>
                  </p>
                </div>
                <div>
                  {medicinePhoto ? (
                    <img
                      src={`https://api.muktihospital.com${medicinePhoto}`}
                      alt="Medicine"
                      className="max-w-96 max-h-48 object-cover rounded-md"
                    />
                  ) : (
                    <img src="https://placehold.co/700x200" alt="Placeholder" />
                  )}
                </div>
              </div>
              <div className="mt-4">
                <div className="flex border-b">
                  <button
                    className={`px-4 py-2 font-semibold ${
                      activeTab === "stock"
                        ? "border-b-2 border-M-primary-color/80 text-M-primary-color/80"
                        : "text-gray-500"
                    }`}
                    onClick={() => setActiveTab("stock")}
                  >
                    Stock
                  </button>
                  <button
                    className={`px-4 py-2 font-semibold ${
                      activeTab === "badStock"
                        ? "border-b-2 border-M-primary-color/80 text-M-primary-color/80"
                        : "text-gray-500"
                    }`}
                    onClick={() => setActiveTab("badStock")}
                  >
                    Bad Stock
                  </button>
                </div>
                <div className="mt-4">
                  {activeTab === "stock" ? (
                    <>
                      <div className="mb-4">
                        <input
                          type="text"
                          className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
                          placeholder="Search by bill no"
                          value={stockSearch}
                          onChange={(e) => {
                            setStockSearch(e.target.value);
                            setStockPage(1);
                          }}
                        />
                      </div>
                      {stockTableContent(
                        paginateData(filteredStockData, stockPage)
                      )}
                      {renderPagination(
                        filteredStockData,
                        stockPage,
                        setStockPage
                      )}
                    </>
                  ) : (
                    <>
                      <div className="mb-4">
                        <input
                          type="text"
                          className="max-w-48 w-full p-2 border rounded-md outline-none focus:ring-0 font-inter font-medium text-sm text-M-heading-color shrink-0"
                          placeholder="Search by batch no"
                          value={badStockSearch}
                          onChange={(e) => {
                            setBadStockSearch(e.target.value);
                            setBadStockPage(1);
                          }}
                        />
                      </div>
                      {badStockTableContent(
                        paginateData(filteredBadStockData, badStockPage)
                      )}
                      {renderPagination(
                        filteredBadStockData,
                        badStockPage,
                        setBadStockPage
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {isEditModalOpen && (
        <EditMedicineModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          onSave={onSave}
          medicine={medicineDetails}
          categories={categories}
          groups={groups}
          companies={companies}
          units={units}
        />
      )}
      <ConfirmActionModal
        isOpen={deleteModalOpen && itemToDelete === null}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={confirmDelete}
        ids={id}
        entityName="medicine"
        title="Confirm Delete Medicine"
        message="Do you really want to delete this medicine?"
        confirmText="Delete"
        cancelText="Cancel"
      />
      <ConfirmActionModal
        isOpen={deleteModalOpen && itemToDelete !== null}
        onClose={() => {
          setDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleRowDelete}
        ids={itemToDelete}
        entityName={activeTab === "stock" ? "stock item" : "bad stock item"}
        title={`Confirm Delete ${activeTab === "stock" ? "Stock Item" : "Bad Stock Item"}`}
        message={`Do you really want to delete this ${activeTab === "stock" ? "stock item" : "bad stock item"}?`}
        confirmText="Delete"
        cancelText="Cancel"
      />
      {isEditBadStockModalOpen && (
        <EditBadStockModal
          isOpen={isEditBadStockModalOpen}
          onClose={() => {
            setIsEditBadStockModalOpen(false);
            setSelectedBadStock(null);
          }}
          onSave={handleSaveBadStock}
          badStock={selectedBadStock}
        />
      )}
    </>
  );
};

export default MedicineDetailsModal;